const DB_NAME = 'candidates-last-upload-db';
const STORE_NAME = 'uploads';
const DB_VERSION = 1;
const TTL = 5 * 60 * 1000;

export type UploadRecord = {
	id?: number;
	fileName: string;
	type: 'addEmployees' | 'resignationEmployees';
	user: string;
	timestamp: number;
};

const openDB = (): Promise<IDBDatabase> => {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);

		request.onupgradeneeded = (event) => {
			const db = (event.target as IDBOpenDBRequest).result;
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				db.createObjectStore(STORE_NAME, {
					keyPath: 'id',
					autoIncrement: true,
				});
				db.createObjectStore('fileNameIndex', { keyPath: 'fileName' });
			}
		};

		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
};

const getStore = async (mode: IDBTransactionMode = 'readonly') => {
	const db = await openDB();
	const tx = db.transaction(STORE_NAME, mode);
	return tx.objectStore(STORE_NAME);
};

export const addUpload = async (record: UploadRecord): Promise<void> => {
	const store = await getStore('readwrite');
	store.add(record);
};

export const getUploadByName = async (
	fileName: string,
): Promise<UploadRecord | null> => {
	const store = await getStore('readonly');
	return new Promise((resolve, reject) => {
		const req = store.openCursor();
		req.onsuccess = (event) => {
			const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
			if (cursor) {
				const value = cursor.value as UploadRecord;
				if (value.fileName === fileName) {
					resolve(value);
					return;
				}
				cursor.continue();
			} else {
				resolve(null);
			}
		};
		req.onerror = () => reject(req.error);
	});
};

export const updateUpload = async (
	fileName: string,
	newTimestamp: number,
): Promise<void> => {
	const store = await getStore('readwrite');
	const req = store.openCursor();

	return new Promise((resolve, reject) => {
		req.onsuccess = (event) => {
			const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
			if (cursor) {
				const record = cursor.value as UploadRecord;
				if (record.fileName === fileName) {
					record.timestamp = newTimestamp;
					cursor.update(record);
					resolve();
					return;
				}
				cursor.continue();
			} else {
				resolve();
			}
		};
		req.onerror = () => reject(req.error);
	});
};

export const cleanOldUploads = async (): Promise<void> => {
	const store = await getStore('readwrite');
	const cutoff = Date.now() - TTL;
	const req = store.openCursor();

	req.onsuccess = (event) => {
		const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
		if (cursor) {
			const record = cursor.value as UploadRecord;
			if (record.timestamp < cutoff) {
				cursor.delete();
			}
			cursor.continue();
		}
	};
};
